import { Box, Tooltip, Typography } from '@mui/material';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ContextStore } from '../../store/contextStore';
import { formatTs } from '../components';
import type { IntegrationFact } from '../integrationTypes';
import { DEVELOPER_PATHS, IMPORTS_PATH, importRunPath } from '../routes';
import { factKind, factValue, importState, METRICS_NOTES } from './integrationLabels';
import { IntegrationsContext } from './IntegrationsContext';

const TIME_FACTS = ['last_import', 'last_poll', 'last_request'];

/** The permission each target page needs: a tile is a link only for users who may open it. */
export const LINK_PERMS: Record<string, [string, string]> = {
    request_log: ['developer', 'read'],
    imports: ['import', 'list'],
};

/** The request log's own parameter for the filter chip's words (never sent to the server). */
export const INTEGRATION_NAME_PARAM = 'integration_name';

/** Where a tile goes: the request log with the tile's filters (and the system's name for its filter chip), or the
 *  import run (else the import list). */
export const factHref = (fact: IntegrationFact, systemName?: string): string | null => {
    if (!fact.link) {
        return null;
    }
    if (fact.link.page === 'imports') {
        return fact.import_run_id ? importRunPath(fact.import_run_id) : IMPORTS_PATH;
    }
    const params = new URLSearchParams(fact.link.query || {});
    if (systemName && params.has('integration')) {
        params.set(INTEGRATION_NAME_PARAM, systemName);
    }
    const qs = params.toString();
    return `${DEVELOPER_PATHS.requests}${qs ? `?${qs}` : ''}`;
};

const Tile = ({ fact, systemName, canOpen, trackedSince }: {
    fact: IntegrationFact; systemName?: string; canOpen: (page: string) => boolean; trackedSince: string | null;
}) => {
    const words = factKind(fact.kind);
    const href = fact.link && canOpen(fact.link.page) ? factHref(fact, systemName) : null;
    const value = factValue(fact, trackedSince);
    const unknown = TIME_FACTS.includes(fact.kind) && !fact.at && !!fact.note && fact.kind !== 'last_request';
    const untracked = TIME_FACTS.includes(fact.kind) && !fact.at && !unknown && trackedSince;
    const state = fact.kind === 'last_import' && fact.at && fact.state && fact.state !== 'success' ? importState(fact.state) : null;
    const alarming = fact.kind === 'rejected_24h' && (fact.value || 0) > 0;
    const body = (
        <>
            <Typography component="dt" variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {words.label}
            </Typography>
            <Typography
                component="dd"
                sx={{ m: 0, fontSize: 18, lineHeight: '24px', fontWeight: 500, fontVariantNumeric: 'tabular-nums', color: alarming ? 'error.main' : 'text.primary' }}
            >
                {TIME_FACTS.includes(fact.kind) && fact.at ? (
                    <Tooltip title={formatTs(fact.at)}><span>{value}</span></Tooltip>
                ) : unknown ? (
                    <Tooltip title="This cannot be read right now: it shows once Curated Planet has finished setting it up."><span>{value}</span></Tooltip>
                ) : untracked ? (
                    <Tooltip title={`None recorded since ${formatTs(trackedSince)}: older history was not recorded.`}><span>{value}</span></Tooltip>
                ) : value}
            </Typography>
            {state && (
                <Typography component="dd" variant="caption" sx={{ m: 0, display: 'block', color: state.tone === 'error' ? 'error.main' : 'text.secondary' }}>
                    {state.label}
                </Typography>
            )}
        </>
    );
    const tile = {
        display: 'block',
        minWidth: 0,
        px: 1.5,
        py: 1.25,
        bgcolor: 'background.paper',
        color: 'inherit',
        textDecoration: 'none',
    };
    return href ? (
        <Box
            component={RouterLink}
            to={href}
            data-testid={`fact-${fact.kind}`}
            aria-label={`${words.label}: ${value}${state ? `, ${state.label}` : ''}. Open ${fact.link?.page === 'imports' ? 'the import' : 'the request log'}`}
            title={words.hint}
            sx={{
                ...tile,
                transition: 'background-color 150ms ease-out',
                '&:hover': { bgcolor: 'action.hover' },
                '&:focus-visible': { outline: (theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: '-2px' },
            }}
        >
            <Box component="dl" sx={{ m: 0 }}>{body}</Box>
        </Box>
    ) : (
        <Box data-testid={`fact-${fact.kind}`} title={words.hint} sx={tile}>
            <Box component="dl" sx={{ m: 0 }}>{body}</Box>
        </Box>
    );
};

/** The card's facts (plan §1.3), as the backend serves them (`facts[]`: no per-vendor code here). Hairlines
 *  between tiles come from the 1px gap over the divider colour, at any column count. */
const FactStrip = ({ facts, systemName }: { facts: IntegrationFact[]; systemName?: string }) => {
    const { hasPerm } = React.useContext(ContextStore) as any;
    const trackedSince = React.useContext(IntegrationsContext)?.trackedSince ?? null;
    if (!facts.length) {
        return null;
    }
    const canOpen = (page: string) => {
        const perm = LINK_PERMS[page];
        return !!perm && !!hasPerm?.(perm[0], perm[1]);
    };
    // The metrics line speaks for the webhook facts; a last import's own note is said on its tile.
    const note = facts.filter((f) => f.kind !== 'last_import').map((f) => f.note).find(Boolean);
    return (
        <Box sx={{ my: 1.5 }}>
            <Box
                data-testid="fact-strip"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: `repeat(${Math.min(facts.length, 4)}, minmax(0, 1fr))` },
                    gap: '1px',
                    bgcolor: 'divider',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
            >
                {facts.map((fact) => <Tile key={fact.kind} fact={fact} systemName={systemName} canOpen={canOpen} trackedSince={trackedSince} />)}
                {/* An odd count on two columns: fill the last cell, so no grey hole shows. */}
                {facts.length % 2 === 1 && <Box sx={{ display: { xs: 'block', sm: 'none' }, bgcolor: 'background.paper' }} />}
            </Box>
            {note && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75 }} data-testid="metrics-note">
                    {METRICS_NOTES[note] || note}
                </Typography>
            )}
        </Box>
    );
};

export default FactStrip;
